// Muoversi a PopTown (artboard 56): dove si può andare, come si arriva e come ci si scansa. I pop
// camminano solo dove si cammina (walk.ts), non attraversano la card del login, scelgono mete
// libere e non si sovrappongono; contro un bordo si fermano e cercano un'altra strada. Quello che
// fanno e con chi parlano sta in sim.ts.
import { CAST, type Place, type Role } from './cast';
import { findPath, randomSpot, walkable, type Grid } from './walk';

export interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

type State = 'idle' | 'walk' | 'talk' | 'stay' | 'play';

export interface Pop {
  id: number;
  role: Role;
  variant: number;
  x: number;
  y: number;
  facing: 1 | -1;
  /** fase del passo: avanza con la strada fatta */
  stride: number;
  moving: boolean;
  state: State;
  timer: number;
  path: { x: number; y: number }[];
  speed: number;
  partner: Pop | null;
  /** cosa fare all'arrivo */
  then: 'meet' | 'place' | 'play' | null;
  place: Place | null;
  bubble: { text: string; left: number } | null;
  stuck: number;
}

export interface Ball {
  from: Pop;
  to: Pop;
  t: number;
  /** secondi di gioco che restano */
  left: number;
}

export const rand = (a: number, b: number) => a + Math.random() * (b - a);
/** più in basso nella scena = più vicino = più grande */
export const depth = (y: number) => 0.8 + (0.3 * y) / 941;
/** spazio personale, in pixel della città a metà profondità: un pop è largo circa 72 */
export const SPACE = 84;
/** peso della distanza verticale: un pop è più alto che largo, e uno davanti all'altro non deve
 * coprirlo; con 0,8 servono circa 105 pixel in verticale contro 84 in orizzontale */
export const TALL = 0.8;

export abstract class Crowd {
  /** cosa fa un pop arrivato alla meta: lo decide la vita sociale (sim.ts) */
  protected abstract arrive(p: Pop): void;

  constructor(
    protected base: Grid,
    count: number,
  ) {
    this.grid = base;
    CAST.slice(0, count).forEach((role, id) => {
      // ognuno nasce lontano da chi c'è già: nessuno parte sovrapposto
      let at = randomSpot(base);
      for (
        let i = 0;
        i < 40 && this.pops.some((o) => Math.hypot(o.x - at.x, o.y - at.y) < 120);
        i++
      )
        at = randomSpot(base);
      this.pops.push({
        id,
        role,
        variant: Math.floor(Math.random() * 5),
        x: at.x,
        y: at.y,
        facing: Math.random() < 0.5 ? 1 : -1,
        stride: Math.random() * 6,
        moving: false,
        state: 'idle',
        timer: rand(0.3, 4),
        path: [],
        speed: rand(42, 58),
        partner: null,
        then: null,
        place: null,
        bubble: null,
        stuck: 0,
      });
    });
  }
  pops: Pop[] = [];
  view: Rect = { x0: 0, y0: 0, x1: 1672, y1: 941 };
  card: Rect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  /** la griglia su cui si cammina: quella della città meno la zona della card */
  protected grid: Grid;

  /** La card del login non si attraversa: i pop ci girano intorno e le nuvolette restano
   * visibili. I piedi stanno fuori dai lati della card e abbastanza sotto il suo bordo basso perché tutta la figura (alta circa 90) resti libera; sopra il bordo
   * alto si può stare, il corpo resta sopra la card. */
  setCard(r: Rect) {
    this.card = r;
    const open = new Uint8Array(this.base.open);
    const { cols, rows } = this.base;
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < cols; x++) {
        const wx = x * 8 + 4;
        const wy = y * 8 + 4;
        if (wx > r.x0 - 62 && wx < r.x1 + 62 && wy > r.y0 + 10 && wy < r.y1 + 95)
          open[y * cols + x] = 0;
      }
    this.grid = { cols, rows, open };
    // chi è rimasto dentro la zona esce dal punto libero più vicino
    for (const p of this.pops) {
      if (walkable(this.grid, p.x, p.y)) continue;
      const out = this.snap(p);
      if (out) Object.assign(p, out);
      p.path = [];
      if (p.state === 'walk') this.cancel(p);
    }
  }

  /** dove ha senso andare: nella parte visibile, fuori dalla card e lontano dagli altri */
  protected spot(near?: { x: number; y: number }, span = 260, me?: Pop) {
    const v = this.view;
    const box = near
      ? {
          x0: Math.max(v.x0, near.x - span),
          y0: Math.max(v.y0, near.y - span),
          x1: Math.min(v.x1, near.x + span),
          y1: Math.min(v.y1, near.y + span),
        }
      : { x0: v.x0 + 40, y0: v.y0 + 60, x1: v.x1 - 40, y1: v.y1 - 20 };
    let best = randomSpot(this.grid, box, this.pad(this.card, 30));
    for (let i = 0; i < 24 && this.crowded(best, me); i++)
      best = randomSpot(this.grid, box, this.pad(this.card, 30));
    return best;
  }

  /** un punto troppo vicino a qualcuno, o a dove qualcuno sta andando */
  protected crowded(q: { x: number; y: number }, me?: Pop, room = SPACE * 1.1): boolean {
    return this.pops.some((o) => {
      if (o === me) return false;
      const end = o.path[o.path.length - 1];
      const near = (a: { x: number; y: number }) =>
        Math.hypot(a.x - q.x, (a.y - q.y) * TALL) < room;
      return near(o) || (end !== undefined && near(end));
    });
  }

  protected pad(r: Rect, m: number): Rect {
    return { x0: r.x0 - m, y0: r.y0 - m, x1: r.x1 + m, y1: r.y1 + m };
  }

  protected snap(p: { x: number; y: number }) {
    for (let r = 0; r < 160; r += 6)
      for (let a = 0; a < 6.28; a += 0.5) {
        const x = p.x + Math.cos(a) * r;
        const y = p.y + Math.sin(a) * r;
        if (walkable(this.grid, x, y)) return { x, y };
      }
    return null;
  }

  protected go(p: Pop, to: { x: number; y: number }, then: Pop['then']): boolean {
    const path = findPath(this.grid, p, to);
    if (!path.length) return false;
    p.path = path;
    p.state = 'walk';
    p.then = then;
    p.stuck = 0;
    return true;
  }

  /** un appuntamento saltato: tutti e due tornano liberi */
  protected cancel(p: Pop) {
    const o = p.partner;
    for (const q of o ? [p, o] : [p]) {
      q.partner = null;
      q.then = null;
      q.path = [];
      q.state = 'idle';
      q.timer = rand(0.3, 1.5);
    }
  }

  protected say(p: Pop, text: string) {
    p.bubble = { text, left: 1.6 + text.length * 0.06 };
  }

  protected inView(q: { x: number; y: number }) {
    const v = this.view;
    const c = this.pad(this.card, 20);
    return (
      q.x > v.x0 + 30 &&
      q.x < v.x1 - 30 &&
      q.y > v.y0 + 70 &&
      q.y < v.y1 - 10 &&
      !(q.x > c.x0 && q.x < c.x1 && q.y > c.y0 && q.y < c.y1)
    );
  }

  protected walk(p: Pop, dt: number) {
    const target = p.path[0];
    if (!target) return this.arrive(p);
    const dx = target.x - p.x;
    const dy = target.y - p.y;
    const dist = Math.hypot(dx, dy);
    if (dist < 4) {
      p.path.shift();
      return;
    }
    const sp = p.speed * depth(p.y) * dt;
    let vx = (dx / dist) * sp;
    let vy = (dy / dist) * sp;
    // spazio personale: si scansa chi è davanti e si rallenta se il passaggio è occupato
    for (const o of this.pops) {
      if (o === p) continue;
      const ox = p.x - o.x;
      const oy = (p.y - o.y) * TALL;
      const d = Math.hypot(ox, oy);
      const r = SPACE * 1.25 * depth(p.y);
      if (d > 0 && d < r) {
        const ahead = (dx * -ox + dy * -oy) / (dist * d);
        const push = ((r - d) / r) * sp * 1.4;
        vx += (ox / d) * push + (ahead > 0.5 ? (-dy / dist) * push : 0);
        vy += (oy / d / TALL) * push + (ahead > 0.5 ? (dx / dist) * push : 0);
        if (ahead > 0.85 && d < r * 0.6) {
          vx *= 0.3;
          vy *= 0.3;
        }
      }
    }
    const bx = p.x;
    const by = p.y;
    const nx = p.x + vx;
    const ny = p.y + vy;
    if (walkable(this.grid, nx, ny)) {
      p.x = nx;
      p.y = ny;
    } else if (walkable(this.grid, p.x + (dx / dist) * sp, p.y + (dy / dist) * sp)) {
      p.x += (dx / dist) * sp;
      p.y += (dy / dist) * sp;
    }
    const step = Math.hypot(p.x - bx, p.y - by);
    const moved = step > sp * 0.2;
    // il passo si anima solo se si va davvero avanti: contro un bordo si sta fermi
    p.moving = moved;
    if (moved) {
      p.stride += (step / depth(p.y)) * 0.22;
      // il verso segue dove si vuole andare, non le spinte di chi si scansa
      if (Math.abs(dx) > 2) p.facing = dx > 0 ? 1 : -1;
    }
    // bloccato: dopo un secondo si cerca un'altra strada, o un'altra meta
    p.stuck = moved ? 0 : p.stuck + dt;
    if (p.stuck > 1) {
      const end = p.path[p.path.length - 1];
      if (!end || Math.random() < 0.5 || !this.go(p, end, p.then)) this.cancel(p);
    }
  }

  /** Nessuno si sovrappone: chi è troppo vicino si sposta. Chi parla o gioca resta fermo e sono
   * gli altri a girargli intorno; vicino ai bordi si prova anche il solo spostamento orizzontale
   * o verticale. */
  protected separate(dt: number) {
    const k = Math.min(1, dt * 8);
    for (const a of this.pops)
      for (const b of this.pops) {
        // i compagni si separano solo finché camminano: in scena stanno già alla loro distanza
        if (a.id >= b.id || (a.partner === b && a.state !== 'walk' && b.state !== 'walk')) continue;
        const dx = b.x - a.x;
        const dy = (b.y - a.y) * TALL;
        const d = Math.hypot(dx, dy) || 0.01;
        const min = SPACE * depth((a.y + b.y) / 2);
        if (d >= min) continue;
        const fixedA = a.state === 'talk' || a.state === 'play';
        const fixedB = b.state === 'talk' || b.state === 'play';
        if (fixedA && fixedB) continue;
        const share = fixedA || fixedB ? 1 : 0.5;
        const push = ((min - d) / d) * share * k;
        let shifted = false;
        for (const [p, s, fixed] of [
          [a, -1, fixedA],
          [b, 1, fixedB],
        ] as const) {
          if (fixed) continue;
          const mx = s * dx * push;
          const my = (s * dy * push) / TALL;
          for (const [tx, ty] of [
            [mx, my],
            [mx * 1.4, 0],
            [0, my * 1.4],
          ]) {
            if (walkable(this.grid, p.x + tx, p.y + ty)) {
              p.x += tx;
              p.y += ty;
              shifted = true;
              break;
            }
          }
        }
        // contro un bordo nessuno si sposta: chi è fermo e libero va da un'altra parte
        if (!shifted && d < min * 0.7)
          for (const p of [a, b]) if (p.state === 'idle' && !p.partner) p.timer = 0;
      }
  }
}
